import { useContext, useState, useMemo } from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Box,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Badge,
  VStack,
  HStack,
  Flex,
  Divider,
  SimpleGrid,
} from '@chakra-ui/react';

import { ExternalLinkIcon, ViewIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';

import { AuthContext } from 'contexts/AuthContext';

import { apiPolicyOverview } from 'api';

import type {
  APIPolicyOverview,
  APIPolicyOverviewFileCategory,
} from 'types/api.types';

import {
  POLICY_METADATA_STATUS_FAILED,
  ROLE_ADMIN,
  ROLE_UNDERWRITER,
  RUNNER_VALIDATE_AUTO_QUOTE,
  RUNNER_VALIDATE_HOME_QUOTE,
  RUNNER_MC_HOME,
  RUNNER_MC_AUTO,
  RUNNER_LEGACY_AUTO,
  RUNNER_MC_LIFE,
  RUNNER_COMMERCIAL_PROPERTY_CASUALTY,
  RUNNER_COMMERCIAL_AUTO,
} from 'variables/constants';

import { useFeatureFlag } from 'hooks/useFeatureFlag';

import FilePreviewModal from 'views/policy_overview/components/FilePreviewModal';
import ValidatedPolicy from 'views/validated_policy/ValidatedPolicyView';

import {
  getUserName,
  isApiUrl,
  openFileWithAuth,
} from 'views/policy_overview/utils';
import StickyNavigation from 'views/binder_report/components/StickyNavigation';

const CATEGORY_LABELS: Partial<Record<APIPolicyOverviewFileCategory, string>> =
  {
    generated_documents: 'Generated Documents',
    policy_documents: 'Policy Documents',
    vehicle_documents: 'Vehicle Documents',
    driver_documents: 'Driver Documents',
    endorsements: 'Endorsements',
    appeal_documents: 'Appeal Documents',
    other_documents: 'Other Documents',
  };

const CATEGORY_ORDER: APIPolicyOverviewFileCategory[] = [
  'generated_documents',
  'policy_documents',
  'vehicle_documents',
  'driver_documents',
  'endorsements',
  'appeal_documents',
  'other_documents',
];

const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex
    direction={{ base: 'column', sm: 'row' }}
    justify="space-between"
    align={{ base: 'flex-start', sm: 'center' }}
    gap={1}
    w="full"
  >
    <Text fontSize="sm" fontWeight="medium" flexShrink={0}>
      {label}
    </Text>
    {children}
  </Flex>
);

const FileActions = ({
  file,
  canDownload,
  isPreviewable,
  isRestricted = false,
  onPreview,
  justify = 'flex-start',
}: {
  file: { file: string | null; name: string };
  canDownload: boolean;
  isPreviewable: boolean;
  isRestricted?: boolean;
  onPreview: () => void;
  justify?: 'flex-start' | 'flex-end';
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!file.file) return;
    if (isApiUrl(file.file)) {
      setIsDownloading(true);
      try {
        await openFileWithAuth(file.file);
      } catch {
        console.error('Failed to open file');
      } finally {
        setIsDownloading(false);
      }
    } else {
      window.open(file.file, '_blank');
    }
  };

  if (isRestricted) {
    return (
      <HStack spacing={2} justify={justify} flexShrink={0}>
        <Badge colorScheme="gray" fontSize="xs" px={2} py={1}>
          Restricted
        </Badge>
      </HStack>
    );
  }

  return (
    <HStack spacing={2} justify={justify} flexShrink={0}>
      {canDownload && file.file && (
        <Button
          size="xs"
          variant="link"
          color="teal.500"
          fontSize="sm"
          fontWeight="medium"
          onClick={handleDownload}
          isLoading={isDownloading}
          rightIcon={<ExternalLinkIcon />}
        >
          Download
        </Button>
      )}
      {isPreviewable && (
        <Button
          size="xs"
          leftIcon={<ViewIcon />}
          onClick={onPreview}
          variant="ghost"
          colorScheme="teal"
        >
          Preview
        </Button>
      )}
    </HStack>
  );
};

const PolicyOverviewView = () => {
  const { user } = useContext(AuthContext);
  const { uuid } = useParams();
  const navigate = useNavigate();
  const isHomeBinderEnabled = useFeatureFlag('enable-home-binder-report');

  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('');

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const {
    isOpen: isValidationsOpen,
    onOpen: onValidationsOpen,
    onClose: onValidationsClose,
  } = useDisclosure();

  const { error, data, isLoading } = useQuery({
    queryKey: ['policy-overview', uuid],
    queryFn: () => apiPolicyOverview(uuid),
  }) as {
    data: APIPolicyOverview;
    isLoading: boolean;
    error: any;
    refetch: any;
  };

  const openFilePreview = (index: number) => {
    setSelectedFileIndex(index);
  };

  const closeFilePreview = () => {
    setSelectedFileIndex(null);
  };

  const isPreviewableFile = (fileName: any) => {
    const lowerFileName = fileName.toLowerCase();
    return (
      lowerFileName.endsWith('.pdf') ||
      /\.(jpg|jpeg|png|gif|webp)$/i.test(lowerFileName)
    );
  };

  const stepToViewableFile = (direction: 1 | -1) => {
    const total = allFiles.length;
    if (total === 0) return;
    let index = selectedFileIndex;
    for (let i = 0; i < total; i += 1) {
      index = (index + direction + total) % total;
      if (!allFiles[index].is_restricted) {
        setSelectedFileIndex(index);
        return;
      }
    }
  };

  const handleOnApplicationFilePreview = () => stepToViewableFile(-1);

  const handleOnApplicationFileNext = () => stepToViewableFile(1);

  const handleOnBinderView = () => {
    if (data.metadata.status === POLICY_METADATA_STATUS_FAILED) {
      navigate(
        `/admin/policies/submission/${data.submission.id}/${uuid}/validations`,
      );
      return;
    }

    navigate(`/admin/policies/submission/${data.submission.id}/${uuid}`);
  };

  const handleNavigateToReport = () => {
    navigate(`/admin/policies/report/${uuid}`);
  };

  const handleNavigateToBillingDetails = () => {
    navigate(`/admin/policies/payments/${uuid}`);
  };

  const isSpecialtyPolicy =
    data?.runner === RUNNER_VALIDATE_AUTO_QUOTE ||
    data?.runner === RUNNER_VALIDATE_HOME_QUOTE;
  const isHomePolicy = data?.runner === RUNNER_MC_HOME;
  const isAutoPolicy = data?.runner === RUNNER_MC_AUTO;
  const isLifePolicy = data?.runner === RUNNER_MC_LIFE;
  const isCommercialPolicy =
    data?.runner === RUNNER_COMMERCIAL_PROPERTY_CASUALTY ||
    data?.runner === RUNNER_COMMERCIAL_AUTO;
  const showValidationsButton = isLifePolicy || isCommercialPolicy;
  const isLegacyAutoPolicy = data?.runner === RUNNER_LEGACY_AUTO;
  const isStandardPolicy = !isSpecialtyPolicy && !isLegacyAutoPolicy;

  const showBillingButton =
    [ROLE_UNDERWRITER, ROLE_ADMIN].includes(user.role) &&
    (isStandardPolicy || isLegacyAutoPolicy);

  const showBinderButton =
    [ROLE_UNDERWRITER, ROLE_ADMIN].includes(user.role) &&
    isStandardPolicy &&
    ((isHomeBinderEnabled && isHomePolicy) || isAutoPolicy);

  const allFiles = useMemo(() => {
    return (data?.application_files || []).map((file, index) => ({
      ...file,
      globalIndex: index,
    }));
  }, [data?.application_files]);

  const documentsByCategory = useMemo(() => {
    const result = {} as Record<
      APIPolicyOverviewFileCategory,
      Array<(typeof allFiles)[0]>
    >;
    allFiles.forEach((file) => {
      const category = file.category || 'other_documents';
      if (!result[category]) {
        result[category] = [];
      }
      result[category].push(file);
    });
    return result;
  }, [allFiles]);

  const categoryIds = useMemo(
    () =>
      CATEGORY_ORDER.filter(
        (category) => documentsByCategory[category]?.length > 0,
      ),
    [documentsByCategory],
  );

  const navSections = useMemo(
    () =>
      categoryIds.map((category) => ({
        id: `category-${category}`,
        label: CATEGORY_LABELS[category],
      })),
    [categoryIds],
  );

  if (isLoading) {
    return <Card textAlign="center">Loading policy...</Card>;
  }

  if (error) {
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  if (!data || !data.submission) {
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Not Found</AlertTitle>
          <AlertDescription>
            The policy is no longer associated with a lead. If a policy was not
            removed from a lead submission, please contact support. Reference
            ID: {uuid}
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Card bg={cardBg} p={8}>
        <HStack justify="space-between" align="center" mb={6}>
          <Heading size="md" color={textColor}>
            Binder Overview
          </Heading>
          <HStack spacing={3}>
            {showBillingButton && (
              <Button
                variant="outline"
                colorScheme="teal"
                onClick={handleNavigateToBillingDetails}
                size="sm"
              >
                Billing Details
              </Button>
            )}
            {showBinderButton && (
              <Button
                variant="outline"
                colorScheme="teal"
                onClick={handleNavigateToReport}
                size="sm"
              >
                Binder Report
              </Button>
            )}
            {isStandardPolicy && (
              <>
                {!showValidationsButton && (
                  <Button
                    leftIcon={<ViewIcon />}
                    colorScheme="teal"
                    variant="outline"
                    onClick={onValidationsOpen}
                    size="sm"
                  >
                    Validations
                  </Button>
                )}
                <Button
                  colorScheme="teal"
                  onClick={handleOnBinderView}
                  size="sm"
                  leftIcon={<ViewIcon />}
                >
                  Application Form
                </Button>
              </>
            )}
            {(isSpecialtyPolicy || isLegacyAutoPolicy) && (
              <Button
                colorScheme="teal"
                onClick={() =>
                  navigate(
                    `/admin/policies/submission/${data.submission.id}/${uuid}/${
                      isSpecialtyPolicy ? 'specialty' : 'legacy-auto'
                    }`,
                  )
                }
                size="sm"
                leftIcon={<ViewIcon />}
              >
                {isSpecialtyPolicy ? 'Specialty Form' : 'Legacy Auto Form'}
              </Button>
            )}
          </HStack>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={6}>
          <Box p={5} border="1px" borderColor={borderColor} borderRadius="lg">
            <VStack align="start" spacing={3}>
              <Heading size="sm" color="gray.500" textTransform="uppercase">
                Details
              </Heading>
              <VStack align="start" spacing={2} w="full">
                <InfoRow label="Policy No.:">
                  <Text fontSize="sm" fontWeight="bold">
                    {data.metadata.policy_nr || '-'}
                  </Text>
                </InfoRow>
                <InfoRow label="Created:">
                  <Text fontSize="sm">{data.date}</Text>
                </InfoRow>
                <InfoRow label="Validation status:">
                  <Badge
                    colorScheme="gray"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                  >
                    {data.metadata.status_display}
                  </Badge>
                </InfoRow>
              </VStack>
            </VStack>
          </Box>

          <Box p={5} border="1px" borderColor={borderColor} borderRadius="lg">
            <VStack align="start" spacing={3}>
              <Heading size="sm" color="gray.500" textTransform="uppercase">
                Policy
              </Heading>
              <VStack align="start" spacing={2} w="full">
                <InfoRow label="Broker:">
                  <Text fontSize="sm">{data.metadata.broker || '-'}</Text>
                </InfoRow>
                <InfoRow label="Carrier:">
                  <Text fontSize="sm">
                    {data.metadata.carrier?.name || '-'}
                  </Text>
                </InfoRow>
                <InfoRow label="Premium:">
                  <Text fontSize="sm">{data.metadata.premium || '-'}</Text>
                </InfoRow>
              </VStack>
            </VStack>
          </Box>

          {isStandardPolicy && (
            <Box p={5} border="1px" borderColor={borderColor} borderRadius="lg">
              <VStack align="start" spacing={3}>
                <Heading size="sm" color="gray.500" textTransform="uppercase">
                  Binder PDFs
                </Heading>
                <VStack align="start" spacing={2} w="full">
                  {data.binder_pdf?.status === 1 && (
                    <InfoRow label="Binder PDF:">
                      <Badge colorScheme="yellow" fontSize="xs">
                        Generating...
                      </Badge>
                    </InfoRow>
                  )}
                  {data.binder_pdf?.status === 3 && (
                    <InfoRow label="Binder PDF:">
                      <Badge colorScheme="red" fontSize="xs">
                        Generation Failed
                      </Badge>
                    </InfoRow>
                  )}

                  {(() => {
                    const docs = documentsByCategory.generated_documents || [];
                    const hasSignedDoc = docs.some(
                      (f) =>
                        f.type === 'E-Signed Document' ||
                        f.type === 'Wet Signed Document',
                    );
                    return docs
                      .filter(
                        (file) => !hasSignedDoc || file.type !== 'Binder PDF',
                      )
                      .map((file) => {
                        const isSigned =
                          file.type === 'E-Signed Document' ||
                          file.type === 'Wet Signed Document';
                        return (
                          <InfoRow key={file.name} label="Binder PDF:">
                            <Flex
                              gap={2}
                              justify={{ base: 'flex-start', sm: 'flex-end' }}
                              align="center"
                              wrap="wrap"
                            >
                              <Badge
                                colorScheme={isSigned ? 'green' : 'orange'}
                                fontSize="xs"
                                py={1}
                              >
                                {isSigned ? 'Signed' : 'Unsigned'}
                              </Badge>
                              <FileActions
                                file={file}
                                canDownload={data.can_download}
                                isPreviewable={isPreviewableFile(file.name)}
                                isRestricted={file.is_restricted}
                                onPreview={() =>
                                  openFilePreview(file.globalIndex)
                                }
                                justify="flex-end"
                              />
                            </Flex>
                          </InfoRow>
                        );
                      });
                  })()}

                  {documentsByCategory.policy_documents?.map((file) => (
                    <InfoRow key={file.name} label={`${file.type}:`}>
                      {data.has_policy_documents_expired ? (
                        <Badge colorScheme="red" fontSize="xs">
                          Expired
                        </Badge>
                      ) : (
                        <FileActions
                          file={file}
                          canDownload={data.can_download}
                          isPreviewable={isPreviewableFile(file.name)}
                          isRestricted={file.is_restricted}
                          onPreview={() => openFilePreview(file.globalIndex)}
                          justify="flex-end"
                        />
                      )}
                    </InfoRow>
                  ))}

                  {!documentsByCategory.generated_documents?.length &&
                    !documentsByCategory.policy_documents?.length &&
                    !data.binder_pdf && (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">
                        No PDFs generated yet
                      </Text>
                    )}
                </VStack>
              </VStack>
            </Box>
          )}
        </SimpleGrid>
      </Card>
      <div
        style={{
          background: cardBg,
          borderRadius: '24px',
          padding: '24px',
          boxShadow: '14px 17px 40px 4px rgba(112, 144, 176, 0.08)',
        }}
      >
        {navSections.length > 1 && (
          <StickyNavigation
            sections={navSections}
            activeSection={activeCategory}
            onActiveChange={setActiveCategory}
          />
        )}

        <Card bg={cardBg} pt={6} px={0} shadow="none">
          <VStack align="start" spacing={4}>
            {data.application_files.length === 0 ? (
              <Text color="gray.500" fontStyle="italic">
                No documents available
              </Text>
            ) : (
              <VStack spacing={6} w="full" align="stretch">
                {categoryIds.map((category) => (
                  <Box key={category} id={`category-${category}`}>
                    <Heading size="sm" color="gray.600" mb={4}>
                      {CATEGORY_LABELS[category]}
                    </Heading>
                    <SimpleGrid
                      columns={{ base: 1, md: 2, lg: 3 }}
                      spacing={4}
                      w="full"
                    >
                      {documentsByCategory[category].map((file) => (
                        <Box
                          key={file.name}
                          p={4}
                          border="1px"
                          borderColor={borderColor}
                          borderRadius="md"
                          w="full"
                        >
                          <VStack align="start" spacing={3}>
                            <HStack
                              justify="space-between"
                              w="full"
                              align="start"
                            >
                              <Badge
                                colorScheme="blue"
                                fontSize="xs"
                                whiteSpace="normal"
                                wordBreak="break-word"
                                textAlign="center"
                                maxW="60%"
                              >
                                {file.type}
                              </Badge>
                              <Text fontSize="xs" color="gray.500">
                                By {getUserName(file.user)}
                              </Text>
                            </HStack>

                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color={textColor}
                              noOfLines={2}
                              title={file.name}
                            >
                              {file.name}
                            </Text>

                            {data.has_policy_documents_expired &&
                            category === 'policy_documents' ? (
                              <Badge colorScheme="red" fontSize="xs">
                                Expired
                              </Badge>
                            ) : (
                              <FileActions
                                file={file}
                                canDownload={data.can_download}
                                isPreviewable={isPreviewableFile(file.name)}
                                isRestricted={file.is_restricted}
                                onPreview={() =>
                                  openFilePreview(file.globalIndex)
                                }
                              />
                            )}
                          </VStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                ))}
              </VStack>
            )}
          </VStack>
        </Card>
      </div>

      {data.additional_notes && (
        <Card bg={cardBg} p={6}>
          <VStack align="start" spacing={4}>
            <Heading size="md" color={textColor}>
              Additional Notes
            </Heading>
            <Divider />
            <Text color="gray.700" whiteSpace="pre-wrap">
              {data.additional_notes}
            </Text>
          </VStack>
        </Card>
      )}

      <FilePreviewModal
        file={
          allFiles[selectedFileIndex]?.is_restricted
            ? null
            : allFiles[selectedFileIndex]
        }
        onClose={closeFilePreview}
        onPrevFile={handleOnApplicationFilePreview}
        onNextFile={handleOnApplicationFileNext}
        canDownload={data.can_download}
      />

      <Modal isOpen={isValidationsOpen} onClose={onValidationsClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="6xl">
          <ModalHeader>Policy Validations</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <ValidatedPolicy
              policyUuid={uuid}
              submissionId={data.submission.id}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default PolicyOverviewView;
